/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import { useAnalyticsStore } from '../../stores/analytics/analytics.store';
import { postEvent } from './event';
import { getViewerCountryHeader, getViewerPlatform } from './viewer';

const initAnalyticsStore = (): void => {
  const { initSession } = useAnalyticsStore.getState();
  initSession();
};

export const resetAnalyticsStore = (): void => {
  const { resetSession } = useAnalyticsStore.getState();
  resetSession();
};

export const sessionStartEvent = async (): Promise<Response | null> => {
  const { sessionId } = useAnalyticsStore.getState();

  if (sessionId) {
    return null;
  }

  try {
    initAnalyticsStore();

    const response = await postEvent({
      eventType: 'session_start',
    });

    if (response.status !== 204) {
      resetAnalyticsStore();
    }

    return response;
  } catch (error) {
    resetAnalyticsStore();
    throw error;
  }
};

let sessionEndPromise: Promise<Response | null> | null = null;

export const sessionEndEvent = async (): Promise<Response | null> => {
  if (sessionEndPromise) {
    // Return the ongoing promise if already running
    return sessionEndPromise;
  }

  sessionEndPromise = (async () => {
    const { sessionExpiration, sessionId } = useAnalyticsStore.getState();
    const inactivityEnd = Date.now() >= sessionExpiration;

    if (!sessionId) {
      sessionEndPromise = null;
      return null;
    }

    const response = await postEvent({
      eventType: 'session_end',
      data: {
        is_inactivity_end: inactivityEnd,
      },
    });

    if (response.status === 204 || (response.status >= 400 && response.status < 500)) {
      resetAnalyticsStore();
    }

    sessionEndPromise = null;
    return response;
  })();

  return sessionEndPromise;
};

export const sessionInfoEvent = async (): Promise<Response | null> => {
  const { sessionId, userConsent, sessionInfoSent } = useAnalyticsStore.getState();

  if (!sessionId || !userConsent || sessionInfoSent) {
    return null;
  }

  const platform = getViewerPlatform();
  const country = await getViewerCountryHeader();

  const response = await postEvent({
    eventType: 'session_info',
    data: {
      country,
      platform,
    },
  });

  if (response.status === 204) {
    useAnalyticsStore.setState({ sessionInfoSent: true });
  }

  return response;
};
